import { useEffect, useMemo } from 'react';
import { useError } from 'react-use';

import { useRestPostQuery } from 'utils/hooks/api/useRestPostQuery';
import {
  BEHAVIORAL_INSIGHTS_API,
  CUSTOM_BEHAVIOR_PREFIXES,
  CUSTOM_BEHAVIOR_REGEX,
} from '../../constants';
import {
  InsightQueryRequest,
  BehaviorQuery,
  BehaviorQueryKeysMap,
  RawBehaviorQuery,
} from '../../types';
import { useInsightsResponseHandler } from './useInsightsResponseHandler';

export const isCustomBehaviorMetric = (event: string) =>
  CUSTOM_BEHAVIOR_PREFIXES.some((conversionType) => event.startsWith(conversionType));

const getCustomBehaviorMetric = (key: string) => {
  const match = key.match(CUSTOM_BEHAVIOR_REGEX);
  return match ? match[1] : '';
};

export const useBehavioralInsightsQuery = ({
  searchQuery,
  emailListId,
  endDate,
  startDate,
}: InsightQueryRequest) => {
  const params = useMemo(
    () => ({
      searchQuery,
      emailListId,
      endDate,
      startDate,
    }),
    [searchQuery, emailListId, endDate, startDate],
  );

  const { handleResponse, data = {}, isRetrying } = useInsightsResponseHandler<RawBehaviorQuery>();

  const { refetch, isLoading, ...results } = useRestPostQuery<
    RawBehaviorQuery,
    InsightQueryRequest
  >(
    BEHAVIORAL_INSIGHTS_API,
    params,
    {
      enabled: false,
      retry: (failureCount, error) => error.response?.status === 206,
      onError: useError(),
    },
    undefined,
    handleResponse,
  );

  //  Query requires explicit refetch call to fetch, only executes for existing list
  useEffect(() => {
    if (params?.emailListId) {
      refetch();
    }
  }, [params, refetch]);

  const modifiedBehavior: BehaviorQuery = (
    Object.keys(BehaviorQueryKeysMap) as Array<keyof RawBehaviorQuery>
  ).reduce(
    (aggregator: Partial<BehaviorQuery>, key) => ({
      ...aggregator,
      [BehaviorQueryKeysMap[key]]: data[key],
    }),
    {},
  );

  const customBehavior: BehaviorQuery = (Object.keys(data) as Array<keyof RawBehaviorQuery>).reduce(
    (aggregator: Partial<BehaviorQuery>, key) =>
      isCustomBehaviorMetric(key)
        ? { ...aggregator, [getCustomBehaviorMetric(key)]: data[key] }
        : aggregator,
    {},
  );

  return {
    ...results,
    refetch,
    isLoading: isLoading && !isRetrying,
    data: { ...modifiedBehavior, ...customBehavior },
  };
};
